// Minnesotaänger Mini App — main layout.
// Restored to telegram-tt's Main DOM contract: `#Main` carries the
// `left-column-open` / `left-column-animating` classes that Main.scss keys
// the mobile slide navigation on (≤925px the LeftColumn is a fixed drawer
// and the MiddleColumn slides over it; ≤600px the left column is
// `100vw - safe-area` and the middle column slides to `100vw`). v1 derives
// the left column visibility from the active chat: on mobile it is open
// while no chat is active, on desktop both columns are always shown. The
// animating class is set for the duration of the slide so Main.scss only
// enables `transition: transform` during it.

import '../../global/actions/all';

import { memo, useLayoutEffect, useRef } from '@teact';

import type { ElementRef } from '@teact';

import { withGlobal } from '../../global';
import { selectActiveChatId } from '../../global/selectors';
import { selectDialog } from '../../global/selectors/miniapp';

import useAppLayout from '../../hooks/useAppLayout';
import useForceUpdate from '../../hooks/useForceUpdate';
import useShowTransition from '../../hooks/useShowTransition';
import useSyncEffect from '../../hooks/useSyncEffect';
import { setBootStage, getBootStage, conversationsEnded } from '../../util/bootStage';

import Dialog from '../ui/Dialog';
import LeftColumn from '../left/LeftColumn';
import MiddleColumn from '../middle/MiddleColumn';

import './Main.scss';

type StateProps = {
  chatId?: string;
  // Contract-level failure dialogs (§3.4) render at the root, above both
  // columns, exactly where SwiftUI's `.alert` lives in the view hierarchy.
  dialog?: { title?: string; message: string } | null;
};

const LEFT_COLUMN_TRANSITION_MS = 350;

function Main({ chatId, dialog }: StateProps) {
  const { isMobile } = useAppLayout();

  // v1: the left column is open when no chat is active on mobile; on
  // desktop both columns are always visible.
  const isLeftColumnOpen = !chatId || !isMobile;

  const containerRef = useRef<HTMLDivElement>();
  const leftColumnRef = useRef<HTMLDivElement>();
  const willAnimateLeftColumnRef = useRef(false);
  const forceUpdate = useForceUpdate();

  useShowTransition({
    ref: containerRef,
    isOpen: isLeftColumnOpen,
    noMountTransition: true,
    prefix: 'left-column-',
  });

  useSyncEffect(([prevIsLeftColumnOpen]) => {
    if (prevIsLeftColumnOpen === undefined || isLeftColumnOpen === prevIsLeftColumnOpen) {
      return undefined;
    }

    willAnimateLeftColumnRef.current = true;

    const timeout = window.setTimeout(() => {
      willAnimateLeftColumnRef.current = false;
      forceUpdate();
    }, LEFT_COLUMN_TRANSITION_MS);

    return () => window.clearTimeout(timeout);
  }, [isLeftColumnOpen]);

  // v1 (fix-list B): while a chat is open, Telegram's vertical swipe gestures
  // (pull-to-close) would fight the message list scroll, so they are
  // disabled; returning to the chat list re-enables them. Safe fallback:
  // absent SDK methods (desktop browser, old SDK) are skipped.
  useSyncEffect(() => {
    const tgWebApp = (window as typeof window & {
      Telegram?: { WebApp?: { disableVerticalSwipes?: () => void; enableVerticalSwipes?: () => void } };
    }).Telegram?.WebApp;
    if (!tgWebApp) return undefined;
    if (chatId) {
      tgWebApp.disableVerticalSwipes?.();
    } else {
      tgWebApp.enableVerticalSwipes?.();
    }
    return undefined;
  }, [chatId]);

  // v1: boot diagnostics — Main 挂载完成即推进阶段链；若会话数据链路已结束
  // （如重挂载场景），直接置 ready。
  useLayoutEffect(() => {
    if (containerRef.current && document.getElementById('Main')) {
      setBootStage('main-mounted');
      if (conversationsEnded(getBootStage())) {
        setBootStage('ready');
      }
    }
  }, []);

  return (
    <div
      id="Main"
      ref={containerRef}
      className={willAnimateLeftColumnRef.current ? 'left-column-animating' : undefined}
    >
      <LeftColumn ref={leftColumnRef} isFoldersSidebarShown={false} />
      <MiddleColumn leftColumnRef={leftColumnRef} isMobile={isMobile} />
      {dialog && <Dialog title={dialog.title} message={dialog.message} />}
    </div>
  );
}

export default memo(withGlobal<StateProps>((global): StateProps => ({
  chatId: selectActiveChatId(global),
  dialog: selectDialog(global),
}))(Main));
